<template>
  <div id="app all-requests">
    <div v-if="!loaded" class="loader">
      <div class="loading loading-lg"></div>
    </div>

    <template v-else>
      <div class="scroll-top" @click="scrollToTop">(^)</div>

      <div class="toast toast-warning" v-if="!updating && !processing && (needsRescrape || isExchangeOpen)">
        <span v-if="needsRescrape">
          Please re-scrape to populate this page!
        </span>
      </div>

      <!-- FILTERS -->

      <div role="search" class="filters mt-2">
        <div class="text-assistive">Search filters</div>

        <div class="form-horizontal columns">
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="fandom-filter">Fandom:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="fandom-filter" placeholder="Search fandoms..."
                v-model="fandomFilter" />
            </div>
          </div>
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="freeform-filter">Freeforms:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="freeform-filter" placeholder="Search freeforms..."
                v-model="freeformFilter" />
            </div>
          </div>
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="optional-tags-filter">Optional Tags:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="optional-tags-filter"
                placeholder="Search optional tags..." v-model="optionalTagFilter" />
            </div>
          </div>
        </div>

        <div class="form-horizontal columns">
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="character-filter">Characters:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="character-filter" placeholder="Search characters..."
                v-model="characterFilter" />
            </div>
          </div>
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="relationship-filter">Relationships:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="relationship-filter"
                placeholder="Search relationships..." v-model="relationshipFilter" />
            </div>
          </div>

          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="prompt-filter">Prompt:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="prompt-filter" placeholder="Search prompt text..."
                v-model="promptFilter" />
            </div>
          </div>
        </div>

        <div class="form-horizontal columns">
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="character-filter">Archive Warnings:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="warning-filter" placeholder="Search warnings..."
                v-model="warningFilter" />
            </div>
          </div>
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="ratings-filter">Ratings:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="ratings-filter" placeholder="Search ratings..."
                v-model="ratingFilter" />
            </div>
          </div>
          <div class="form-group column col-4">
            <div class="col-5 col-sm-12">
              <label class="form-label label-sm" for="user-filter">Username:
              </label>
            </div>
            <div class="col-7 col-sm-12">
              <input class="form-input input-sm" type="text" id="user-filter" placeholder="Search username..."
                v-model="usernameFilter" />
            </div>
          </div>
        </div>

        <span>
          <button ref="permalink" class=" mr-2 btn btn-sm permalink" @click="copyShareableLink"><span
              class="fas fa-link"></span>
            <span class="fas fa-check"></span>
            Permalink</button>
        </span>
        <label class="form-switch form-inline">
          <input type="checkbox" v-model="addOptionals" />
          <i class="form-icon"></i> Search for Optionals in Freeforms as well
        </label>
        <label class="form-switch form-inline">
          <input type="checkbox" v-model="sortByFandom" />
          <i class="form-icon"></i> Sort by fandom
        </label>
        <label class="form-switch form-inline">
          <input type="checkbox" v-model="dedupeByFandom" />
          <i class="form-icon"></i> De-dupe requests with multiple fandoms
        </label>



        <p class="text-small">
          This view loads <em>all</em> prompts into memory and can slow down your device for large challenges. Search
          terms are
          AND by default. You can use syntax like | for OR, ! for NOT, and
          ="exact match". Prompt meme requests will have a best-guess link to the AO3 page the prompt might be on - the
          more recent the scrape, the better the guess.
        </p>
      </div>

      <button class=" mr-2 btn btn-sm" @click="openDetails">

        Expand Details</button>

      <div class="divider"></div>

      <!-- END FILTERS -->

      <div v-if="updating" class="loader">
        <div class="loading loading-lg"></div>
      </div>

      <div class="toast toast-warning" v-if="processing && exchangeId">
        The <strong>last successful scrape is being shown while a new
          scrape
          is in-progress</strong>. Caveat lector. Prompt bookmarking is disabled until after signups are closed. Someone
        must re-scrape
        again
        at that time.
      </div>

      <pagination :pagination="pagination" @page="page"></pagination>

      <table class="table table-striped" v-if="!updating" aria-label="Requests table">
        <thead>
          <tr>
            <th class="prompts">Requests</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="prompt in filtered" :key="prompt['sha']">
            <td class="prompt">
              <p>
                <strong>Username</strong>: {{ prompt.username }}

              </p>
              <p>
                <strong>Fandom(s)</strong>: {{ prompt.fandoms && prompt.fandoms.length > 1 ? prompt.fandoms.join(', ') :
                  prompt.fandom }}

              </p>

              <p v-if="prompt.title">
                <strong>
                  Title</strong>: {{ prompt.title }}
              </p>

              <p v-if="prompt.characters && prompt.characters.length">
                <strong>
                  Characters</strong>: {{ prompt.characters.join(', ') }}
              </p>

              <p v-if="prompt.relationships && prompt.relationships.length">
                <strong>
                  Relationships</strong>: {{ prompt.relationships.join(', ') }}
              </p>

              <p v-if="prompt.warnings && prompt.warnings.length">
                <strong>
                  Warnings</strong>: {{ prompt.warnings.join(', ') }}
              </p>

              <p v-if="prompt.ratings && prompt.ratings.length">
                <strong>
                  Ratings</strong>: {{ prompt.ratings.join(', ') }}
              </p>

              <p v-if="prompt.freeforms && prompt.freeforms.length">
                <strong>
                  Freeforms</strong>: {{ prompt.freeforms.join(', ') }}
              </p>

              <p v-if="prompt.optional_tags && prompt.optional_tags.length">
                <strong>
                  Optional Tags</strong>: {{ prompt.optional_tags.join(', ') }}
              </p>

              <p v-if="prompt.letter">
                <strong>
                  Letter</strong>: {{ prompt.letter }}
              </p>

              <div v-html="prompt.prompt" class="prompt-text"></div>

              <hr class="break" />
              <!-- <span v-else>No prompts ):</span> -->
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <pagination :pagination="pagination" @page="page"></pagination>

  </div>
</template>

<script>
import utils from "../components/utils.js";
import chips from "../components/chips.vue";
import characterList from "../components/character-list.vue";
import Pagination from "../components/pagination.vue";

import {
  uniqWith,
  debounce,
  intersection,
  sortBy,
  each,
  includes,
  remove
} from "lodash-es";
import { mapGetters } from "vuex";
import axios from "axios";
import Fuse from "fuse.js";
const delay = ms => new Promise(res => setTimeout(res, ms));
let fandomSearch;
let freeformSearch;
let relationshipSearch;
let optionalTagsSearch;
let charactersSearch;
let promptSearch;
let warningSearch;
let ratingSearch;
let userSearch;
function getFn(obj, path) {
  //   // Use the default `get` function
  const value = Fuse.config.getFn(obj, path);
  //   // ... do something with `value`

  if (Array.isArray(value)) {
    return value.map(el => normalizeStr(el));
  }
  return normalizeStr(value);
}
const fuseOptions = {
  shouldSort: true,
  threshold: 0,
  ignoreLocation: true,
  useExtendedSearch: true,
  getFn
};

// Remove english articles from fandom names
function removeArticlesCompare(o) {
  if (!o) {
    return;
  }
  const regex = /^(the\s|a\s|an\s)/i;
  if (!o.name) {
    return o;
  }
  return o.name.toLowerCase().replace(regex, "");
}

function normalizeStr(str) {
  if (!str || typeof str !== "string") {
    return;
  }
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export default {
  name: "all",
  components: { chips, characterList, Pagination },
  beforeMount() {
    this.$store.commit("setAllRequests", []);
    this.load();
  },
  created() {
    this.loadFandomBookmarksAndPromptmarks();
    this.updateFilter();
    // window.addEventListener("scroll", this.handleScroll);
  },
  // destroyed() {
  //   window.removeEventListener("scroll", this.handleScroll);
  // },
  data() {
    return {
      scraperRes: undefined,
      scraperLoading: false,
      processing: false,
      showPrompt: [],
      status: {},
      open: true,
      filtered: [],
      updating: true,
      sticky: false,
      loaded: false,
      fandomFilter: undefined,
      freeformFilter: undefined,
      relationshipFilter: undefined,
      optionalTagFilter: undefined,
      characterFilter: undefined,
      promptFilter: undefined,
      warningFilter: undefined,
      ratingFilter: undefined,
      usernameFilter: undefined,
      scrollIndex: 50,
      totalFound: 0,
      addOptionals: true,
      sortByFandom: false,
      dedupeByFandom: false,
      pagination: {
        first: 0,
        last: 100,
        size: 100,
        total: 0
      },
    };
  },
  computed: {
    ...mapGetters([
      "exchangeId",
      "isExchangeOpen",
      "allRequests",
      "bookmarks",
      "promptmarks",
      "timestamp",
      "options",
    ]),
    needsRescrape() {
      return new Date(this.timestamp) < new Date("2021-07-08T00:00:00.000Z");
    }
  },
  watch: {
    $route(to) {
      if (to.name === "allrequests") {
        this.$store.commit("setExchangeId", this.$route.params.exchangeId);
        this.scrollIndex = 50;
        this.totalFound = 0;

        this.load();
      }
    },
    fandomFilter() {
      this.updateFilter();
    },
    characterFilter() {
      this.updateFilter();
    },
    freeformFilter() {
      this.updateFilter();
    },
    optionalTagFilter() {
      this.updateFilter();
    },
    relationshipFilter() {
      this.updateFilter();
    },
    promptFilter() {
      this.updateFilter();
    },
    warningFilter() {
      this.updateFilter();
    },
    ratingFilter() {
      this.updateFilter();
    },
    usernameFilter() {
      this.updateFilter();
    },
    dedupeByFandom() {
      this.updateFilter();
    },
    addOptionals(val) {
      if (val) {
        optionalTagsSearch = freeformSearch = new Fuse(this.allRequests, {
          keys: ["optional_tags", "freeforms"],
          ...fuseOptions
        });
      } else {
        optionalTagsSearch = new Fuse(this.allRequests, {
          keys: ["optional_tags"],
          ...fuseOptions
        });
        freeformSearch = new Fuse(this.allRequests, {
          keys: ["freeforms"],
          ...fuseOptions
        });
      }

      this.updateFilter();
    },
    sortByFandom() {
      this.updateFilter();
    },
    options: {
      handler(val) {
        this.$store.commit("setOptions", val);
        this.$localStorage.set("appOptions", JSON.stringify(val));
      },
      deep: true
    }
  },
  methods: {
    ...utils,
    sortBy,
    page(start) {
      this.pagination.last = start + this.pagination.size;
      this.pagination.first = start;
      this.updateFilter();
    },

    openDetails() {
      const detailsElements = document.querySelectorAll('details');

      detailsElements.forEach(detail => {
        detail.setAttribute('open', '');
      });
    },
    handleScroll() {
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = document.documentElement;

      if (
        scrollTop + clientHeight >= scrollHeight - 5 &&
        this.allRequests.length > this.scrollIndex
      ) {
        const newIndex = this.scrollIndex + 50;
        if (newIndex < this.allRequests.length) {
          this.scrollIndex = newIndex;
        } else {
          this.scrollIndex = this.allRequests.length;
        }

        this.updateFilter();
      }
    },
    load() {
      this.status = {};
      this.scraperRes = undefined;
      this.scraperLoading = false;
      this.processing = false;
      this.updating = false;
      this.loaded = false;
      this.$store.commit("setAllRequests", []);

      this.updateFilterFromParams(this.$router.currentRoute);

      this.filtered = [];

      const exchangeId = this.$route.params.exchangeId;
      this.$store.commit("setExchangeId", this.$route.params.exchangeId);

      axios
        .get(
          `https://autoao3.herokuapp.com/db/exchanges/${exchangeId}/status`
        )
        .then(res => {
          if (
            res.data &&
            res.data.status != "open" &&
            res.data.status != "complete" &&
            res.data.status != "unparseable"
          ) {
            this.processing = true;
            this.status = res.data;
          }

          this.$store.commit("setTimestamp", res.data.scraped);

          this.$store.commit(
            "setIsExchangeOpen",
            res.data && (res.data.status == "open" || res.data.status == "processing")
          );
        });

      // Auctionbot
      if (exchangeId == 'AuctionsAsThreatened' || exchangeId == 'AAT_Request_Overflow') {

        const otherExchange = exchangeId == 'AuctionsAsThreatened' ? 'AAT_Request_Overflow' : 'AuctionsAsThreatened';
        axios
          .get(
            `https://autoao3.herokuapp.com/db/exchange/${exchangeId}`
          )
          .then(res => {
            let result = res.data


            axios.get(
              `https://autoao3.herokuapp.com/db/exchange/${otherExchange}`

            )
              .then(res2 => {
                result = result.concat(res2.data);
                this.$store.commit("setAllRequests", result);

                this.loaded = true;
                fandomSearch = new Fuse(result, {
                  keys: ["fandom"],
                  ...fuseOptions
                });
                relationshipSearch = new Fuse(result, {
                  keys: ["relationships"],
                  ...fuseOptions
                });
                freeformSearch = optionalTagsSearch = new Fuse(result, {
                  keys: ["optional_tags", "freeforms"],
                  ...fuseOptions
                });

                charactersSearch = new Fuse(result, {
                  keys: ["characters"],
                  ...fuseOptions
                });

                // optionalTagsSearch = new Fuse(result, {
                //   keys: ["optionalTags"],
                //   ...fuseOptions
                // });

                userSearch = new Fuse(result, {
                  keys: ["username"],
                  ...fuseOptions
                });

                promptSearch = new Fuse(result, {
                  keys: ["prompt"],
                  ...fuseOptions
                });

                warningSearch = new Fuse(result, {
                  keys: ["warnings"],
                  ...fuseOptions
                });

                ratingSearch = new Fuse(result, {
                  keys: ["ratings"],
                  ...fuseOptions
                });

                userSearch = new Fuse(result, {
                  keys: ["username"],
                  ...fuseOptions
                });

                this.updateFilter();

              })

          });

      } else {

        axios
          .get(
            `https://autoao3.herokuapp.com/db/exchange/${exchangeId}`
          )
          .then(res => {
            let result = res.data
            this.$store.commit("setAllRequests", result);

            this.loaded = true;
            fandomSearch = new Fuse(result, {
              keys: ["fandom"],
              ...fuseOptions
            });
            relationshipSearch = new Fuse(result, {
              keys: ["relationships"],
              ...fuseOptions
            });
            freeformSearch = optionalTagsSearch = new Fuse(result, {
              keys: ["optional_tags", "freeforms"],
              ...fuseOptions
            });

            charactersSearch = new Fuse(result, {
              keys: ["characters"],
              ...fuseOptions
            });

            // optionalTagsSearch = new Fuse(result, {
            //   keys: ["optionalTags"],
            //   ...fuseOptions
            // });

            userSearch = new Fuse(result, {
              keys: ["username"],
              ...fuseOptions
            });

            promptSearch = new Fuse(result, {
              keys: ["prompt"],
              ...fuseOptions
            });

            warningSearch = new Fuse(result, {
              keys: ["warnings"],
              ...fuseOptions
            });

            ratingSearch = new Fuse(result, {
              keys: ["ratings"],
              ...fuseOptions
            });

            userSearch = new Fuse(result, {
              keys: ["username"],
              ...fuseOptions
            });

            this.updateFilter();
          });

      }


      this.loadFandomBookmarksAndPromptmarks();
    },
    togglePrompt(sha) {
      if (this.shouldShowPrompt(sha)) {
        this.showPrompt = remove(this.showPrompt, sha);
      } else {
        this.showPrompt.push(sha);
      }
    },
    shouldShowPrompt(sha) {
      return includes(this.showPrompt, sha);
    },
    updateFilter: debounce(function (resetPagination) {
      this.updating = true;
      let arr = this.allRequests;

      if (resetPagination) {
        this.pagination.first = 0;
        this.pagination.last = this.pagination.size;
      }

      // Do not let pagination go below 0
      if (this.pagination.first < 0) {
        this.pagination.first = 0;
      }


      if (this.fandomFilter && this.fandomFilter.length) {
        const indices = fandomSearch.search(normalizeStr(this.fandomFilter));
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = res;
      }

      if (this.relationshipFilter && this.relationshipFilter.length) {
        const indices = relationshipSearch.search(
          normalizeStr(this.relationshipFilter)
        );
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.characterFilter && this.characterFilter.length) {
        const indices = charactersSearch.search(
          normalizeStr(this.characterFilter)
        );
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.optionalTagFilter && this.optionalTagFilter.length) {
        const indices = optionalTagsSearch.search(
          normalizeStr(this.optionalTagFilter)
        );
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.freeformFilter && this.freeformFilter.length) {
        const indices = freeformSearch.search(
          normalizeStr(this.freeformFilter)
        );
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }
      if (this.promptFilter && this.promptFilter.length) {
        const indices = promptSearch.search(normalizeStr(this.promptFilter));
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }

      if (this.warningFilter && this.warningFilter.length) {
        const indices = warningSearch.search(normalizeStr(this.warningFilter));
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }

      if (this.ratingFilter && this.ratingFilter.length) {
        const indices = ratingSearch.search(normalizeStr(this.ratingFilter));
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }

      if (this.usernameFilter && this.usernameFilter.length) {
        const indices = userSearch.search(normalizeStr(this.usernameFilter));
        const res = [];
        each(indices, i => {
          res.push(this.allRequests[i.refIndex]);
        });
        arr = intersection(arr, res);
      }

      if (this.sortByFandom) {
        arr = sortBy(arr, p => p.fandom.toLowerCase(), [removeArticlesCompare]);

      } else {
        arr = sortBy(arr, p => p.username.toLowerCase(), [removeArticlesCompare]);
      }

      if (this.dedupeByFandom) {
        arr = uniqWith(arr, (a, b) => {
          return a.request_id == b.request_id
        })
      }

      this.totalFound = arr.length;

      this.filtered = arr.slice(this.pagination.first, this.pagination.last);
      this.pagination.total = arr.length;

      this.updating = false;
    }, 300),
    scrollToTop() {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    process() {
      this.scraperLoading = true;
      axios
        .get(`https://autoao3.herokuapp.com/scrape/${this.exchangeId}`)
        .then(res => {
          this.scraperRes = res;
          this.scraperLoading = false;
        })
        .catch(e => {
          if (e.response) {
            this.scraperRes = e.response;
            this.scraperLoading = false;
            return;
          }
          this.scraperRes = {
            error: true,
            e: e,
            msg: "Something went wrong! Report a bug."
          };
          this.scraperLoading = false;
        });
    },
    async copyShareableLink() {

      let params = [];
      if (this.fandomFilter) {
        params.push(`fandom=${this.fandomFilter}`)
      }
      if (this.freeformFilter) {
        params.push(`freeforms=${this.freeformFilter}`)
      }
      if (this.characterFilter) {
        params.push(`characters=${this.characterFilter}`)
      }
      if (this.relationshipFilter) {
        params.push(`relationships=${this.relationshipFilter}`)
      }
      if (this.promptFilter) {
        params.push(`prompt=${this.promptFilter}`)
      }
      if (this.optionalTagFilter) {
        params.push(`optionaltags=${this.optionalTagFilter}`)
      }
      if (this.ratingFilter) {
        params.push(`rating=${this.ratingFilter}`)
      }
      if (this.warningFilter) {
        params.push(`warnings=${this.warningFilter}`)
      }
      if (this.usernameFilter) {
        params.push(`username=${this.usernameFilter}`)
      }

      const queryparams = params.length ?
        `?${params.join('&')}`
        : ''


      // this.$refs.copyInput.value = `${window.location.origin}/#/${this.exchangeId}/all?${queryparams}`
      this.$refs.copyInput.value = `https://autoao3app.fandom.tools/#/${this.exchangeId}/all?${queryparams}`
      // Select the text field
      this.$refs.copyInput.select();
      this.$refs.copyInput.setSelectionRange(0, 99999); // For mobile devices

      // Copy the text inside the text field
      navigator.clipboard.writeText(this.$refs.copyInput.value);

      this.$refs.permalink.classList.add("active");
      await delay(400);

      this.$refs.permalink.classList.remove("active");


    }
  }
};
</script>

<style lang="scss">
@import "../styles/app.scss";
@import "../styles/mobile.scss";
</style>


<style lang="scss" scoped>
@import "../styles/variables.scss";

.prompt {
  margin: 0.8em 0;
  // border-bottom: 1px solid $outline-light;

  &:last-child {
    .break {
      display: none;
    }
  }
}

.bookmark,
.letter {
  height: 1.3em;
}

.user-data {
  font-size: larger;

  .letter {
    padding: 0;
    margin: 0;
    vertical-align: baseline;
  }

  .bookmark {
    position: relative;
    bottom: 4px;
    margin-right: 0.4em;
  }
}

.flex {
  margin-top: 0.4em;
  display: flex;
}

ul {
  li {
    margin: 0;
    padding: 4px 0;
    border-bottom: 1px solid $outline-light;

    &:last-child {
      border: 0;
    }

    &:first-child {
      padding-top: 0;
    }
  }
}

.flex-25,
.requested-characters {
  // margin-top: 1em;
  // margin-right: 1em;
  flex: 0 0 25%;
}

.toggle {
  width: 150px;
  height: 35px;
  display: block;
}

.break {
  width: 80%;
  margin: 0.8em auto;
}

.form-group.col-4 {
  &.form-group:not(:last-child) {
    margin-bottom: 0;
  }
}

@media screen and (max-width: 767px) {
  .flex {
    display: inherit;
  }

  .requested-characters {
    display: block;
    margin-bottom: 0.5em;
  }
}

#copyInput {
  display: none;
}

.permalink {
  .fa-check {
    display: none;
  }

  &.active {

    .fa-link {
      display: none;
    }

    .fa-check {
      display: inline;
    }
  }
}
</style>